import { useState, forwardRef, useEffect, useImperativeHandle } from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../../../utils/utils";
import { EyeIcon, EyeOff } from "lucide-react";
import StarIcon from "../../../assets/svgs/common/star.svg";

const inputVariants = cva(
  "focus:ring-gap-1 px-6 py-6 border rounded-xl w-full font-figtree font-medium text-base tracking-[-0.32px] outline-none",
  {
    variants: {
      variant: {
        default: "border-black bg-white text-black focus:ring-2 ring-gray-300",
        error: "border-red-500 bg-white text-black ",
        disabled: "border-gray-300 bg-gray-100 text-gray-500",
        "3d": "border-black bg-white text-black relative z-10", // New 3D variant
      },
      size: {
        default: "px-6 py-6 text-base",
        sm: "p-2 text-base",
        lg: "p-4 text-xl",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

export interface InputProps
  extends Omit<React.InputHTMLAttributes<HTMLInputElement>, "size">,
    VariantProps<typeof inputVariants> {
  label?: string;
  error?: string;
  fullWidth?: boolean;
  validateAs?: "email" | "required" | "none";
  onValidationChange?: (isValid: boolean, errorMessage?: string) => void;
  forceValidate?: boolean;
  prefixIcon?: React.ReactNode; // Add this prop for prefix icon
}

// Export a ref type that allows parent components to trigger validation
export interface InputRef {
  validate: () => boolean;
  getValue: () => string;
}

const Input = forwardRef<InputRef, InputProps>(
  (
    {
      label,
      type = "text",
      variant,
      size,
      className,
      error: externalError,
      fullWidth = true,
      disabled,
      validateAs = "none",
      onValidationChange,
      forceValidate = false,
      prefixIcon, // Add this prop
      onWheel,
      ...props
    },
    ref
  ) => {
    const [value, setValue] = useState(props.defaultValue || "");
    const [showPassword, setShowPassword] = useState(false);
    const [internalError, setInternalError] = useState<string | undefined>(
      undefined
    );
    const [touched, setTouched] = useState(false);

    const isPassword = type === "password";
    const inputType = isPassword ? (showPassword ? "text" : "password") : type;

    // Determine if there's an error (either external or internal)
    const error = externalError || internalError;

    // Validate the input based on validateAs prop
    const validateInput = (inputValue: string): boolean => {
      if (
        validateAs === "required" &&
        (!inputValue || inputValue.trim() === "")
      ) {
        setInternalError("This field is required");
        onValidationChange?.(false, "This field is required");
        return false;
      }

      if (validateAs === "email" && inputValue) {
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(inputValue)) {
          setInternalError("Invalid email address");
          onValidationChange?.(false, "Invalid email address");
          return false;
        }
      }

      setInternalError(undefined);
      onValidationChange?.(true);
      return true;
    };

    // Expose methods to parent components via ref
    useImperativeHandle(ref, () => ({
      validate: () => {
        setTouched(true);
        return validateInput(
          props.value !== undefined
            ? (props.value as string)
            : (value as string)
        );
      },
      getValue: () =>
        props.value !== undefined ? (props.value as string) : (value as string),
    }));

    // Validate on value change if already touched
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      if (touched && validateAs !== "none") {
        validateInput(
          props.value !== undefined
            ? (props.value as string)
            : (value as string)
        );
      }
    }, [value, props.value, touched]);

    // Force validation when forceValidate prop changes to true
    // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
    useEffect(() => {
      if (forceValidate && validateAs !== "none") {
        setTouched(true);
        validateInput(
          props.value !== undefined
            ? (props.value as string)
            : (value as string)
        );
      }
    }, [forceValidate]);

    return (
      <div className={cn("flex flex-col gap-2", fullWidth ? "w-full" : "")}>
        {label && (
          <label className="flex font-cabinetgrotesk md:font-medium text-sm md:text-lg leading-none tracking-[-0.36px]">
            {label} {props.required && <StarIcon/>}
          </label>
        )}
        <div className="relative">
          {variant === "3d" && !disabled && !error && (
            <div className="top-0.5 left-0.5 absolute bg-black rounded-xl w-full h-full"></div>
          )}

          {/* Add prefix icon if provided */}
          {prefixIcon && (
            <div className="top-1/2 left-4 z-20 absolute flex items-center -translate-y-1/2 pointer-events-none">
              {prefixIcon}
            </div>
          )}

          <input
            type={inputType}
            value={props.value !== undefined ? props.value : value}
            onChange={(e) => {
              const newValue = e.target.value;
              setValue(newValue);
              props.onChange && props.onChange(e);
            }}
            onBlur={(e) => {
              setTouched(true);
              if (validateAs !== "none") {
                validateInput(
                  props.value !== undefined
                    ? (props.value as string)
                    : (value as string)
                );
              }
              props.onBlur && props.onBlur(e);
            }}
            onWheel={type === "number" ? (e) => { e.currentTarget.blur(); onWheel?.(e); } : onWheel}
            disabled={disabled}
            className={cn(
              inputVariants({
                variant: disabled ? "disabled" : error ? "error" : variant,
                size,
                className,
              }),
              prefixIcon ? "md:pl-12 pl-8" : "", // Add left padding when prefix icon is present
              isPassword && "pr-12"
            )}
            {...props}
          />

          {isPassword && (
            // biome-ignore lint/a11y/useKeyWithClickEvents: <explanation>
            <div
              className="top-1/2 right-6 absolute text-soloprimary -translate-y-1/2 cursor-pointer"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOff size={20} /> : <EyeIcon size={20} />}
            </div>
          )}
        </div>
        {error && (
          <span className="font-cabinetgrotesk text-red-500 text-xs">
            {error}
          </span>
        )}
      </div>
    );
  }
);

Input.displayName = "Input";

export default Input;
